import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Briefcase, Zap, CarFront, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { PageHero } from '../components/motion/PageHero';
import { Reveal, RevealText } from '../components/motion/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const galleryOf = (v) => {
  const g = Array.isArray(v.gallery) ? v.gallery.filter(Boolean) : [];
  if (g.length) return g;
  return v.image ? [v.image] : [];
};

const VehicleRow = ({ vehicle, index }) => {
  const images = galleryOf(vehicle);
  const [active, setActive] = useState(0);
  const reversed = index % 2 === 1;
  const main = images[active];

  return (
    <Reveal
      className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center"
      amount={0.15}
      data-testid={`fleet-row-${vehicle.id}`}
    >
      {/* Gallery */}
      <div className={`lg:col-span-7 ${reversed ? 'lg:order-2' : ''}`}>
        <div className="relative overflow-hidden h-[320px] sm:h-[460px] lg:h-[580px] border border-white/10 bg-white/[0.03]">
          {main ? (
            <img
              key={main}
              src={main}
              alt={vehicle.name}
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03] oc-fade-in"
              data-testid={`fleet-main-image-${vehicle.id}`}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-4">
              <CarFront strokeWidth={1} className="h-20 w-20 text-white/10" />
              <span className="text-[11px] uppercase tracking-[0.25em] text-white/30">Image coming soon</span>
            </div>
          )}
          {vehicle.isElectric && (
            <span className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/25 bg-black/50 backdrop-blur-sm text-[11px] uppercase tracking-[0.2em] text-white">
              <Zap className="h-3 w-3" /> Electric
            </span>
          )}
        </div>

        {images.length > 1 && (
          <div className="mt-4 grid grid-cols-4 gap-3" data-testid={`fleet-thumbs-${vehicle.id}`}>
            {images.map((src, i) => (
              <button
                key={src}
                type="button"
                onClick={() => setActive(i)}
                className={`relative h-20 sm:h-24 overflow-hidden border transition-all ${
                  i === active ? 'border-white' : 'border-white/10 opacity-60 hover:opacity-100'
                }`}
                data-testid={`fleet-thumb-${vehicle.id}-${i}`}
                aria-label={`View image ${i + 1}`}
              >
                <img src={src} alt={`${vehicle.name} ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Details */}
      <div className={`lg:col-span-5 ${reversed ? 'lg:order-1' : ''}`}>
        <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
          {vehicle.category || 'Chauffeur Class'}
        </p>
        <h3 className="text-3xl lg:text-4xl font-light tracking-tight text-white mb-8">
          {vehicle.name}
        </h3>

        <div className="flex items-center gap-8 py-6 border-y border-white/10 mb-8">
          <div className="flex items-center gap-3">
            <Users strokeWidth={1} className="h-6 w-6 text-white/40" />
            <div>
              <div className="text-2xl font-light text-white leading-none">{vehicle.passengers}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 mt-1.5">Passengers</div>
            </div>
          </div>
          <div className="h-10 w-px bg-white/10" />
          <div className="flex items-center gap-3">
            <Briefcase strokeWidth={1} className="h-6 w-6 text-white/40" />
            <div>
              <div className="text-2xl font-light text-white leading-none">{vehicle.luggage}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 mt-1.5">Luggage</div>
            </div>
          </div>
        </div>

        {vehicle.description && (
          <p className="text-white/55 leading-relaxed mb-8 max-w-md">{vehicle.description}</p>
        )}

        {Array.isArray(vehicle.features) && vehicle.features.length > 0 && (
          <ul className="space-y-2.5 mb-10 max-w-md" data-testid={`fleet-features-${vehicle.id}`}>
            {vehicle.features.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                <Check className="h-4 w-4 mt-0.5 shrink-0 text-white/50" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}

        <Link to="/booking">
          <Button
            className="rounded-full bg-transparent border border-white text-white hover:bg-white hover:text-black transition-colors duration-300 px-8 h-12"
            data-testid={`book-vehicle-${vehicle.id}-btn`}
          >
            Book This Vehicle
          </Button>
        </Link>
      </div>
    </Reveal>
  );
};

const Fleet = () => {
  const [fleet, setFleet] = useState([]);
  useEffect(() => {
    axios.get(`${API}/fleet`)
      .then((res) => setFleet(res.data.vehicles || []))
      .catch(() => setFleet([]));
  }, []);

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <PageHero image="/fleet/v-class-lineup.png" eyebrow="The Obsidian Collection" title="Our Fleet" subtitle="Experience unparalleled luxury with our meticulously maintained chauffeur-driven fleet" imageOpacity={0.35} />

      {/* Editorial rows with galleries */}
      <section className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16 lg:mb-24 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">The Collection</p>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-white">
              <RevealText text="Every journey, perfectly matched" />
            </h2>
          </div>

          {fleet.length === 0 ? (
            <p className="text-white/40">Loading fleet…</p>
          ) : (
            <div className="space-y-24 lg:space-y-36">
              {fleet.map((vehicle, index) => (
                <VehicleRow key={vehicle.id} vehicle={vehicle} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Our Fleet */}
      <section className="py-24 bg-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4"><RevealText text="Why Our Fleet Stands Out" /></h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Premium vehicles maintained to the highest standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Impeccably Maintained', description: 'Regular servicing and detailed valeting ensure every vehicle is in pristine condition.' },
              { title: 'Latest Models', description: 'Our fleet features the newest luxury models with cutting-edge technology.' },
              { title: 'Luxury Amenities', description: 'WiFi, charging, climate control and complimentary refreshments in every vehicle.' },
            ].map((item, index) => (
              <Reveal key={index} delay={index * 0.12} className="bg-black border border-white/10 hover:border-white/30 transition-colors duration-500 p-8 text-white rounded-lg">
                <div className="text-5xl font-bold text-white/10 mb-4">{String(index + 1).padStart(2, '0')}</div>
                <h3 className="text-2xl font-bold mb-4 text-white">{item.title}</h3>
                <p className="text-white/60">{item.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white text-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6"><RevealText text="Ready to Experience Our Fleet?" /></h2>
          <p className="text-xl text-black/70 mb-12">Book your luxury journey today</p>
          <Link to="/booking">
            <Button className="bg-black text-white hover:bg-black/90 text-lg px-8 py-6 h-auto">Request a Quote</Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Fleet;
